import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from 'antd';
import {
  BellFilled,
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import API from '../config/api';

interface AppointmentReminder {
  _id: string;
  title: string;
  start_at: string;
  end_at?: string;
  description?: string;
  location?: string;
}

interface ReminderItem extends AppointmentReminder {
  acknowledgementKey: string;
}

interface AppointmentReminderToastProps {
  doctorId: string | null;
  isRTL?: boolean;
}

const REMINDER_WINDOW_MINUTES = 2 * 24 * 60;
const STORAGE_LIMIT = 300;

// Three predictable reminder periods per day. A login during any period shows
// that period's reminder immediately; an open dashboard picks up the next one.
function getDailyPeriod(hour: number) {
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

const AppointmentReminderToast: React.FC<AppointmentReminderToastProps> = ({ doctorId, isRTL = false }) => {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState<ReminderItem[]>([]);
  const acknowledgedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!doctorId) {
      setReminders([]);
      return undefined;
    }

    let cancelled = false;
    const storageKey = `doctorAppointmentAcknowledged:${doctorId}`;

    try {
      acknowledgedRef.current = new Set(JSON.parse(localStorage.getItem(storageKey) || '[]'));
    } catch {
      acknowledgedRef.current = new Set();
    }

    const loadReminders = async () => {
      try {
        const res = await axios.get(
          `${API.BASE_URL}${API.ENDPOINTS.DOCTOR_APPOINTMENT_REMINDERS(doctorId)}`,
          { params: { window_minutes: REMINDER_WINDOW_MINUTES } }
        );
        if (cancelled || !res.data?.success) return;

        const now = dayjs();
        const period = getDailyPeriod(now.hour());
        const periodKey = `${now.format('YYYY-MM-DD')}:${period}`;
        const windowEnd = now.add(2, 'day');

        const due: ReminderItem[] = (res.data.appointments || [])
          .filter((item: AppointmentReminder) => {
            const start = dayjs(item.start_at);
            return !start.isBefore(now) && !start.isAfter(windowEnd);
          })
          .map((item: AppointmentReminder) => ({
            ...item,
            acknowledgementKey: `${item._id}:${item.start_at}:${periodKey}`,
          }))
          .filter((item: ReminderItem) => !acknowledgedRef.current.has(item.acknowledgementKey))
          .sort((a: ReminderItem, b: ReminderItem) => dayjs(a.start_at).valueOf() - dayjs(b.start_at).valueOf());

        setReminders(due);
      } catch {
        // Reminders must never interrupt the doctor's dashboard work.
      }
    };

    loadReminders();
    const interval = window.setInterval(loadReminders, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [doctorId]);

  if (!doctorId || reminders.length === 0) return null;

  const acknowledgeReminder = (item: ReminderItem) => {
    const storageKey = `doctorAppointmentAcknowledged:${doctorId}`;
    acknowledgedRef.current.add(item.acknowledgementKey);
    localStorage.setItem(
      storageKey,
      JSON.stringify(Array.from(acknowledgedRef.current).slice(-STORAGE_LIMIT))
    );
    setReminders((previous) => previous.filter(
      (reminder) => reminder.acknowledgementKey !== item.acknowledgementKey
    ));
  };

  const activeReminder = reminders[0];
  const additionalCount = reminders.length - 1;
  const daysUntil = dayjs(activeReminder.start_at).startOf('day').diff(dayjs().startOf('day'), 'day');
  const dateLabel = isRTL
    ? (daysUntil === 0 ? 'موعد اليوم' : daysUntil === 1 ? 'موعد غداً' : 'موعد خلال يومين')
    : (daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : 'In two days');

  return (
    <div className={`appointment-reminder-stack ${isRTL ? 'appointment-reminder-stack--rtl' : ''}`}>
      <section
        className="appointment-reminder-dialog"
        role="dialog"
        aria-modal="false"
        aria-live="polite"
        aria-label={isRTL ? 'تذكير بموعد' : 'Appointment reminder'}
      >
        <header className="appointment-reminder-dialog__header">
          <div className="appointment-reminder-dialog__bell"><BellFilled /></div>
          <div className="appointment-reminder-dialog__heading">
            <span>{isRTL ? 'تذكير من مواعيدي' : 'My appointments reminder'}</span>
            <strong>{dateLabel}</strong>
          </div>
          {additionalCount > 0 && (
            <span className="appointment-reminder-dialog__count">+{additionalCount}</span>
          )}
        </header>

        <div className="appointment-reminder-dialog__body">
          <h3>{activeReminder.title}</h3>
          <div className="appointment-reminder-dialog__schedule">
            <div>
              <CalendarOutlined />
              <span>{dayjs(activeReminder.start_at).format('dddd, D MMMM')}</span>
            </div>
            <div>
              <ClockCircleOutlined />
              <strong>{dayjs(activeReminder.start_at).format('HH:mm')}</strong>
              {activeReminder.end_at && <span>- {dayjs(activeReminder.end_at).format('HH:mm')}</span>}
            </div>
          </div>
          {activeReminder.location && (
            <div className="appointment-reminder-dialog__meta">
              <EnvironmentOutlined />
              <span>{activeReminder.location}</span>
            </div>
          )}
          {activeReminder.description && (
            <p className="appointment-reminder-dialog__description">{activeReminder.description}</p>
          )}
          {additionalCount > 0 && (
            <p className="appointment-reminder-dialog__queue-note">
              {isRTL ? `يوجد ${additionalCount} تذكير آخر بعد هذا` : `${additionalCount} more reminder${additionalCount > 1 ? 's' : ''} after this one`}
            </p>
          )}
        </div>

        <footer className="appointment-reminder-dialog__actions">
          <Button icon={<CalendarOutlined />} onClick={() => navigate('/appointments')}>
            {isRTL ? 'فتح مواعيدي' : 'Open appointments'}
          </Button>
          <Button type="primary" onClick={() => acknowledgeReminder(activeReminder)} autoFocus>
            {isRTL ? 'حسناً، فهمت' : 'OK, got it'}
          </Button>
        </footer>
      </section>
    </div>
  );
};

export default AppointmentReminderToast;
